import { PRODUCT_CONFIG } from "@stb/product-config"
import { Camera, FileText, Route as RouteIcon } from "lucide-react"
import type { ReactNode } from "react"

import {
  AnswerBox,
  CtaBand,
  FaqAccordion,
  SimpleTable,
  SourceLine,
} from "@/brand/components/landing"
import { COMPARE_LINKS, FREE_TOOL_LINKS, GUIDE_LINKS } from "@/brand/nav-model"
import type { NavLink } from "@/brand/nav-model"
import { localizeLinks } from "@/brand/nav-model.zh"
import { useLocale, useLocalHref } from "@/core/i18n/locale-context"
import { gateCta } from "@/core/launch"
import { Button } from "@/ui/button"

import {
  CentsSlider,
  InvoiceIllustration,
  PhoneTripMock,
  ReceiptIllustration,
  RouteBackdrop,
  ShieldIllustration,
  TripIllustration,
} from "./home-art"
import {
  ATO_BUSINESS_KM_URL,
  ATO_RECORDS_CHECKED,
  ATO_RECORDS_URL,
  capKm,
  HOME_FINANCIAL_YEAR,
  HOME_JOBS,
  HOME_PLANS,
  rate,
} from "./home-content"
import { HOME_COPY_EN, type HomeCopy } from "./home-copy"

const tone = {
  success: "bg-success/15 text-success",
  primary: "bg-primary/15 text-primary",
  violet: "bg-accent text-accent-foreground",
} as const

const JOB_ICONS = [RouteIcon, Camera, FileText]
const JOB_ART = [TripIllustration, ReceiptIllustration, InvoiceIllustration]

const Section = ({
  id,
  title,
  alt,
  children,
}: {
  id?: string
  title: string
  alt?: boolean
  children: ReactNode
}) => {
  const headingId = `${id ?? title.toLowerCase().replaceAll(/[^a-z]+/g, "-")}-title`
  return (
    <section id={id} aria-labelledby={headingId} className={alt ? "bg-muted/50" : undefined}>
      <div className="mx-auto grid max-w-6xl gap-6 px-4 py-10 md:px-12 md:py-16">
        <h2 id={headingId} className="text-2xl font-bold md:text-3xl">
          {title}
        </h2>
        {children}
      </div>
    </section>
  )
}

const textLink =
  "focus-visible:ring-ring rounded-sm font-medium underline underline-offset-2 outline-none focus-visible:ring-2"

/** "More on this" link under a summary section, pointing at the topic's own page. */
const MoreLink = ({ href, children }: { href: string; children: ReactNode }) => {
  const localHref = useLocalHref()
  return (
    <p>
      <a href={localHref(href)} className={textLink}>
        {children}
      </a>
    </p>
  )
}

/** Link cards with a title and one line: compare pages, free tools and guides. */
const LinkCards = ({ heading, links }: { heading?: string; links: NavLink[] }) => {
  const localHref = useLocalHref()
  return (
    <div className="grid gap-3">
      {heading && <h3 className="text-lg font-semibold">{heading}</h3>}
      <ul className="grid gap-3 md:grid-cols-3">
        {links.map(({ label, href, description }) => (
          <li key={href}>
            <a
              href={localHref(href)}
              className="border-border bg-card hover:border-primary focus-visible:ring-ring grid h-full min-h-11 content-start gap-1 rounded-xl border p-4 outline-none focus-visible:ring-2"
            >
              <span className="font-semibold">{label}</span>
              {description && <span className="text-muted-foreground text-sm">{description}</span>}
            </a>
          </li>
        ))}
      </ul>
    </div>
  )
}

/** Guides shown on the home page: all but the blog (hidden while it has no posts). */
const HOME_GUIDES = GUIDE_LINKS.filter((l) => l.href !== "/blog" && l.href !== "/guides")

/**
 * Store badges (T813, D-LAUNCH-1): the native apps are not in the stores yet, so these are labelled
 * "Coming soon" in visible text (which is also the accessible text) and are not links or buttons.
 * When listings exist, swap each item for a link with the official badge artwork.
 */
const StoreBadge = ({ name, comingSoon }: { name: string; comingSoon: string }) => (
  <li className="border-border bg-muted/40 inline-flex min-h-11 flex-col justify-center rounded-lg border border-dashed px-4 py-1 text-left leading-tight">
    <span className="text-muted-foreground text-xs">{comingSoon}</span>
    <b className="text-sm">{name}</b>
  </li>
)

/** Home page; `copy` is the English object by default, the Chinese route passes its own. */
export const HomePage = ({ copy = HOME_COPY_EN }: { copy?: HomeCopy }) => {
  const localHref = useLocalHref()
  const locale = useLocale()
  const zh = locale === "zh"
  const trialCta = gateCta({ href: "/login", label: copy.startTrial }, locale)
  const links = (l: NavLink[]) => (zh ? localizeLinks(l) : l)
  const checked = copy.formatCheckedOn(rate.checkedOn)
  const year = copy.displayYear(HOME_FINANCIAL_YEAR)
  return (
    <>
      <section className="bg-card border-border relative isolate overflow-hidden border-b">
        <RouteBackdrop />
        <div className="mx-auto grid max-w-6xl items-center gap-8 px-4 py-10 md:grid-cols-[1.3fr_1fr] md:gap-12 md:px-12 md:py-20">
          <div className="grid justify-items-start gap-4">
            <span className="bg-primary/15 text-primary rounded-full px-3 py-1 text-sm font-medium">
              {copy.trialBadge}
            </span>
            <h1 className="text-4xl leading-tight font-extrabold tracking-tight md:text-5xl">
              {copy.h1}
            </h1>
            <p className="text-muted-foreground max-w-[56ch] text-lg">{copy.lead}</p>
            <div className="flex flex-wrap items-center gap-3">
              <Button asChild size="lg" className="h-11">
                <a href={trialCta.href}>{trialCta.label}</a>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="border-foreground/40 bg-card text-foreground hover:bg-muted h-11"
              >
                <a href="#how-it-works">{copy.seeHow}</a>
              </Button>
            </div>
            <div role="group" aria-label={copy.appsGroupLabel}>
              <ul className="flex flex-wrap gap-3">
                <StoreBadge name="App Store" comingSoon={copy.comingSoon} />
                <StoreBadge name="Google Play" comingSoon={copy.comingSoon} />
              </ul>
            </div>
          </div>
          <PhoneTripMock />
        </div>
      </section>

      <section className="border-border border-b">
        <ul className="mx-auto grid max-w-6xl grid-cols-2 gap-4 px-4 py-6 md:grid-cols-4 md:px-12">
          {[
            [`${rate.cents} c/km`, copy.statLabels.rate(year)],
            [`${capKm} km`, copy.statLabels.cap],
            [copy.recordsFigure, copy.statLabels.records],
            [copy.regionStat.figure, copy.regionStat.label],
          ].map(([figure, label]) => (
            <li key={label} className="grid">
              <b className="text-xl">{figure}</b>
              <span className="text-muted-foreground text-sm">{label}</span>
            </li>
          ))}
        </ul>
      </section>

      <Section title={copy.jobsTitle}>
        <div className="grid gap-10 md:gap-16">
          {HOME_JOBS.map(({ href, tone: t }, i) => {
            const Icon = JOB_ICONS[i]!
            const Art = JOB_ART[i]!
            const { title, body } = copy.jobs[i]!
            return (
              <article
                key={href}
                className="grid items-center gap-6 md:grid-cols-2 md:gap-14 [&:nth-child(even)>div:last-child]:md:order-first"
              >
                <div className="grid content-start justify-items-start gap-3">
                  <span
                    className={`${tone[t]} grid size-11 place-items-center rounded-lg`}
                    aria-hidden="true"
                  >
                    <Icon className="size-6" />
                  </span>
                  <h3 className="text-xl font-bold md:text-2xl">
                    <a href={localHref(href)} className={textLink}>
                      {title}
                    </a>
                  </h3>
                  <p className="text-muted-foreground max-w-[44ch] md:text-lg">{body}</p>
                </div>
                <div className="hidden justify-items-center md:grid">
                  <Art />
                </div>
              </article>
            )
          })}
        </div>
      </Section>

      <Section id="how-it-works" title={copy.howTitle} alt>
        <ol className="grid gap-6 md:grid-cols-3">
          {copy.steps.map(({ title, body }, i) => (
            <li
              key={title}
              className="md:not-last:after:border-warning/70 relative flex gap-4 md:grid md:content-start md:gap-3 md:not-last:after:absolute md:not-last:after:top-[16px] md:not-last:after:left-11 md:not-last:after:w-[calc(100%-1.75rem)] md:not-last:after:border-t-4 md:not-last:after:border-dashed md:not-last:after:content-['']"
            >
              <span
                aria-hidden="true"
                className="bg-primary text-primary-foreground ring-card relative z-10 grid size-9 shrink-0 place-items-center rounded-full font-bold ring-4"
              >
                {i + 1}
              </span>
              <div>
                <h3 className="font-semibold">{title}</h3>
                <p className="text-muted-foreground mt-1">{body}</p>
              </div>
            </li>
          ))}
        </ol>
        <MoreLink href="/how-it-works">{copy.howMore}</MoreLink>
      </Section>

      <Section title={copy.claimTitle}>
        <div className="grid items-start gap-6 md:grid-cols-2">
          <div className="grid gap-3">
            <AnswerBox
              source={
                <SourceLine
                  sources={[
                    { label: copy.claimSources.individuals, href: rate.sourceUrl },
                    { label: copy.claimSources.business, href: ATO_BUSINESS_KM_URL },
                  ]}
                  financialYear={year}
                  checked={checked}
                />
              }
            >
              {copy.claimAnswer({ capKm, year, cents: rate.cents })}
            </AnswerBox>
            <p className="text-muted-foreground text-sm">{copy.lastReviewed(checked)}</p>
          </div>
          <div className="grid gap-4">
            <CentsSlider />
            <SimpleTable
              caption={copy.methodsTable.caption}
              columns={copy.methodsTable.columns}
              rows={copy.methodsTable.rows}
            />
          </div>
        </div>
      </Section>

      <Section title={copy.recordsTitle} alt>
        <div className="grid items-center gap-8 md:grid-cols-[220px_1fr]">
          <div className="hidden md:grid">
            <ShieldIllustration />
          </div>
          <ul className="grid gap-4 md:grid-cols-3">
            <li className="grid content-start gap-1">
              <h3 className="font-semibold">{copy.recordsStored}</h3>
              <p className="text-muted-foreground">
                {copy.recordsStoredBody[0]}
                <a href={localHref("/trust")} className={textLink}>
                  {copy.trustLinkText}
                </a>
                {copy.recordsStoredBody[1]}
              </p>
            </li>
            <li className="grid content-start gap-1">
              <h3 className="font-semibold">{copy.fiveYearTitle}</h3>
              <p className="text-muted-foreground">{copy.fiveYearBody}</p>
              <SourceLine
                sources={[{ label: copy.atoRecordsLabel, href: ATO_RECORDS_URL }]}
                checked={copy.formatCheckedOn(ATO_RECORDS_CHECKED)}
              />
            </li>
            <li className="grid content-start gap-1">
              <h3 className="font-semibold">{copy.exportTitle}</h3>
              <p className="text-muted-foreground">{copy.exportBody}</p>
            </li>
          </ul>
        </div>
      </Section>

      <section id="pricing">
        <div className="mx-auto grid max-w-6xl gap-6 px-4 py-10 md:px-12 md:py-16">
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="text-2xl font-bold md:text-3xl">{copy.pricingTitle}</h2>
            {PRODUCT_CONFIG.pricesDraft && (
              <span className="bg-warning/20 text-foreground rounded-full px-3 py-1 text-sm font-medium">
                {copy.draftBadge}
              </span>
            )}
          </div>
          <div className="grid gap-4 md:grid-cols-3">
            {HOME_PLANS.map(({ price, period, highlight }, i) => {
              const plan = copy.plans[i]!
              return (
                <div
                  key={plan.name}
                  className={`bg-card grid content-start gap-1.5 rounded-xl border p-5 ${highlight ? "border-primary" : "border-border"}`}
                >
                  <b className="text-base">{plan.name}</b>
                  <div>
                    <span className="text-3xl font-bold">{price}</span>
                    <small className="text-muted-foreground">{period && copy.perMonth}</small>
                  </div>
                  <span className="text-muted-foreground text-sm">{plan.note}</span>
                </div>
              )
            })}
          </div>
          {/* GST wording removed until accountant confirms (T643). */}
          {PRODUCT_CONFIG.pricesDraft && (
            <p className="text-muted-foreground text-sm">{copy.draftNote}</p>
          )}
          <MoreLink href="/pricing">{copy.pricingMore}</MoreLink>
        </div>
      </section>

      <Section title={copy.toolsTitle} alt>
        <LinkCards heading={copy.freeToolsHeading} links={links(FREE_TOOL_LINKS)} />
        <LinkCards heading={copy.guidesHeading} links={links(HOME_GUIDES)} />
      </Section>

      <Section title={copy.compareTitle}>
        <LinkCards links={links(COMPARE_LINKS)} />
      </Section>

      <Section id="faq" title={copy.faqTitle} alt>
        <FaqAccordion items={copy.faq} />
        <MoreLink href="/faq">{copy.faqMore}</MoreLink>
      </Section>

      <CtaBand
        title={copy.bandTitle}
        description={copy.bandDescription}
        ctaLabel={copy.startTrial}
        ctaHref="/login"
      />
    </>
  )
}
